import { createFileRoute } from '@tanstack/react-router';
import { CaseStudyLayout, type CaseStudyContent } from '../components/CaseStudyLayout.js';
import { TravelBufferMockup } from '../components/visuals/TravelBufferMockup.js';
import { IconPersonal } from '../components/visuals/IndustryIcons.js';
import { usePageTitle } from '../components/usePageTitle.js';

export const Route = createFileRoute('/for-gardening')({
  component: GardeningPage,
});

function GardeningPage() {
  usePageTitle('Bookings for gardeners & landscapers');
  return <CaseStudyLayout content={content} />;
}

const content: CaseStudyContent = {
  eyebrow: 'For gardeners & landscapers',
  icon: IconPersonal,
  headline: 'Plan a day, not a phone-tree.',
  headlineEmphasis: 'phone-tree',
  subhead:
    'Bookaburra keeps your morning route on one side of town, suggests new times when a job blows out, and reminds you which clients haven’t booked a hedge trim since Easter.',
  Visual: TravelBufferMockup,
  business: {
    name: 'Two Mowers Gardening',
    location: 'Sydney Inner West · 2 staff · 60 jobs a week',
    summary:
      'A fictional 2-staff gardening + lawn-care business covering Sydney’s Inner West. Mix of fortnightly mows, monthly hedge trims, and one-off garden tidies. Sundays are admin and quotes.',
  },
  demoBookingSlug: 'two-mowers',
  dayTimeline: [
    {
      time: '7:00',
      event:
        'Tuesday morning route starts in Marrickville. Six fortnightly mows for Dave, four for Jess, all in the same 3km arc.',
      bookaburraResponse:
        'Nothing to do. The travel-aware allocator clustered the day by suburb when the bookings landed.',
    },
    {
      time: '8:45',
      event:
        'A new client books a fortnightly mow online and asks for "any Tuesday".',
      bookaburraResponse:
        'Only offers Tuesday slots that fit the existing Marrickville/Dulwich Hill route. Lands her on Tuesday 11:30 between two existing jobs.',
    },
    {
      time: '10:00',
      event:
        'A Petersham client opens the picker to add a ride-on mow.',
      bookaburraResponse:
        'Jess’s slots aren’t offered for ride-on jobs — she doesn’t operate the ride-on. Picker shows Dave’s next ride-on-eligible window on a route through Petersham.',
    },
    {
      time: '11:20',
      event:
        'Jess pulls a muscle, can’t finish the afternoon. Four hedge trims left on her sheet.',
      bookaburraResponse:
        'Sick-day reshuffle drafts a plan: two move to Dave’s Wednesday round (same suburb), two slide to next week. Owner approves before clients are notified.',
    },
    {
      time: '13:30',
      event:
        'Follow-up list surfaces 9 long-term hedge-trim clients overdue by more than 6 weeks.',
      bookaburraResponse:
        'One batch of magic-link "ready for a trim?" emails goes out. Four self-serve into next fortnight’s diary by close of business.',
    },
    {
      time: '15:10',
      event:
        'A one-off landscaping enquiry comes in from Bondi — outside the Inner West patch.',
      bookaburraResponse:
        'Postcode whitelist rejects it before a slot is shown. Picker offers the contact form for an off-patch quote.',
    },
  ],
  pains: [
    {
      quote:
        'A client booked the suburb opposite to this morning’s jobs. The whole run zig-zagged across town.',
      explanation:
        'The booking page offered any open slot, not the slot that fit the day already on the calendar.',
    },
    {
      quote:
        'One of the team called in sick and we had no plan for redistributing the day’s jobs.',
      explanation:
        'Reshuffling on the fly meant ringing every client and hoping someone could move.',
    },
    {
      quote:
        'A long-time client hadn’t called for a hedge trim in months. Easy to forget about them.',
      explanation:
        'No prompt to follow up, so regular work quietly slips off the calendar.',
    },
  ],
  solutions: [
    {
      title: 'Travel-aware allocator',
      body: (
        <>
          The next slot offered to a client fits the day&rsquo;s route, not just
          the staff member&rsquo;s spare hour. Less zig-zagging, more jobs done.
        </>
      ),
    },
    {
      title: 'Sick-day reshuffle',
      body: (
        <>
          When someone calls in sick, Bookaburra proposes a redistribution
          across the remaining team. You approve before any client is moved.
        </>
      ),
    },
    {
      title: 'Follow-up list',
      body: (
        <>
          Each service has a recommended frequency. Clients whose last visit is
          overdue show up on a list you can work through on a Sunday.
        </>
      ),
    },
    {
      title: 'Postcode whitelist',
      body: (
        <>
          Set the metro you actually service. Bookings from outside that
          whitelist are turned away before they hit your day.
        </>
      ),
    },
  ],
  industrySlug: 'gardening',
};
